import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Chip,
  Paper,
  Divider,
  IconButton,
  Box,
  Button,
  Grid,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import axios from "axios.js";

function OpsSaleOrderPage() {
  const pageTitle = "Sales Order";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [copied, setCopied] = useState(false);

  const [pageLoaded, setPageLoaded] = useState(false);
  const [sale, setSale] = useState({});

  function singularize(word) {
    if (!word || typeof word !== "string") return word;

    const irregular = {
      people: "person",
      men: "man",
      women: "woman",
      children: "child",
      teeth: "tooth",
      feet: "foot",
      mice: "mouse",
      geese: "goose",
      data: "datum",
      indices: "index",
      analyses: "analysis",
      crises: "crisis",
      diagnoses: "diagnosis",
      theses: "thesis",
      lives: "life",
      wives: "wife",
      knives: "knife",
      leaves: "leaf",
      shelves: "shelf",
      wolves: "wolf",
    };

    const lower = word.toLowerCase();

    if (irregular[lower]) {
      return matchCase(word, irregular[lower]);
    }

    if (lower.endsWith("ies") && lower.length > 3) {
      return matchCase(word, lower.slice(0, -3) + "y");
    }

    if (/(xes|zes|ches|shes|sses)$/.test(lower)) {
      return matchCase(word, lower.slice(0, -2));
    }

    if (lower.endsWith("ves")) {
      return matchCase(word, lower.slice(0, -3) + "f");
    }

    if (lower.endsWith("s") && !lower.endsWith("ss")) {
      return matchCase(word, lower.slice(0, -1));
    }

    return word;
  }

  function matchCase(original, replacement) {
    return original[0] === original[0].toUpperCase()
      ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
      : replacement;
  }

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      try {
        const result = await axios.get(`/agents/ops/sales/${ref}/fetch.json`);
        setShowLoading(false);
        if (result.data?.status === "success") {
          setSale(result.data.data.order);
          setPageLoaded(true);
        } else {
          setSeverity("error");
          setErrMsg(
            result.data.data?.message || "Failed to fetch sales details",
          );
          setOpen(true);
        }
      } catch (error) {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Check your active internet connection."
          }`,
        );
        setOpen(true);
      }
    };

    fetchData();
  }, [ref]);

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const formatCurrency = (value = 0) =>
    `${sale.currency || "₦"}${(Number(value) / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const getStatusColor = (status) => {
    switch (status) {
      case "SUCCESS":
        return "success";
      case "AWAITING-PAYMENT":
        return "info";
      case "PLACED":
      default:
        return "warning";
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const subtotal = sale.amount || 0;
  const discount = sale.discount || 0;
  const paymentFee = sale.payment_fee || 0;
  const chargeAmount = sale.charge_amount || 0;
  const total = subtotal - discount + paymentFee + chargeAmount;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox px={{ xs: 1, sm: 2, md: 3 }} py={1} mb={6}>
        <MDBox display="flex" alignItems="center" mb={1}>
          <Button
            color="dark"
            startIcon={<Icon>west</Icon>}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </MDBox>

        {pageLoaded && (
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5} lg={5}>
                <Box sx={{ position: { md: "sticky" }, top: 24 }}>
                  {/* Top Info Banner Card */}
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: "#0f172a",
                      color: "#fff",
                      // bgcolor: "#fff",
                      // color: "#0f172a",
                      mb: 2,
                    }}
                  >
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <MDTypography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                      >
                        {sale.type || "Sale Order"}
                      </MDTypography>
                      <Chip
                        label={sale.status}
                        color={getStatusColor(sale.status)}
                        size="small"
                        sx={{ fontWeight: "bold", fontSize: "0.7rem" }}
                      />
                    </MDBox>

                    <MDTypography
                      variant="h3"
                      fontWeight="bold"
                      sx={{ color: "#fff", my: 0.5 }}
                    >
                      {formatCurrency(total)}
                    </MDTypography>

                    {sale.status_message && (
                      <MDTypography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                        display="block"
                      >
                        {sale.status_message}
                      </MDTypography>
                    )}

                    <Divider
                      sx={{ my: 2, borderColor: "rgba(255,255,255,0.15)" }}
                    />

                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6} lg={6}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            sx={{ color: "rgba(255,255,255,0.6)" }}
                            display="block"
                          >
                            {singularize(sale.relationship)}
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: "#fff" }}
                          >
                            {sale.entity || "N/A"}
                          </MDTypography>
                        </MDBox>
                      </Grid>

                      <Grid item xs={12} md={6} lg={6}>
                        <MDBox sx={{ textAlign: { xs: "left", md: "right" } }}>
                          <MDTypography
                            variant="caption"
                            sx={{ color: "rgba(255,255,255,0.6)" }}
                            display="block"
                          >
                            Date
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: "#fff" }}
                          >
                            {sale.created}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Summary Breakdown */}
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{ p: 2.5, borderRadius: 3, mb: 3 }}
                  >
                    <MDTypography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={1.5}
                    >
                      Payment Summary
                    </MDTypography>

                    <MDBox display="flex" flexDirection="column" gap={1}>
                      <MDBox display="flex" justifyContent="space-between">
                        <MDTypography variant="button" color="text">
                          Amount
                        </MDTypography>
                        <MDTypography variant="button">
                          {formatCurrency(subtotal)}
                        </MDTypography>
                      </MDBox>

                      {discount > 0 && (
                        <MDBox display="flex" justifyContent="space-between">
                          <MDTypography variant="button" color="text">
                            Discount
                          </MDTypography>
                          <MDTypography variant="button" color="success">
                            -{formatCurrency(discount)}
                          </MDTypography>
                        </MDBox>
                      )}

                      {paymentFee > 0 && (
                        <MDBox display="flex" justifyContent="space-between">
                          <MDTypography variant="button" color="text">
                            Payment Fee
                          </MDTypography>
                          <MDTypography variant="button">
                            {formatCurrency(paymentFee)}
                          </MDTypography>
                        </MDBox>
                      )}

                      <Divider sx={{ my: 1 }} />

                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="subtitle1" fontWeight="bold">
                          Total Amount
                        </MDTypography>
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          color="dark"
                        >
                          {formatCurrency(chargeAmount)}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Paper>
                </Box>
              </Grid>
              <Grid item xs={12} md={7} lg={7}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "grey.50",
                  }}
                >
                  <MDBox flex={1} mr={1} sx={{ overflow: "hidden" }}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                    >
                      Reference
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      fontWeight="regular"
                      sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
                    >
                      {sale.reference}
                    </MDTypography>
                  </MDBox>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(sale.reference)}
                    color={copied ? "success" : "default"}
                  >
                    <Icon fontSize="small">
                      {copied ? "check" : "content_copy"}
                    </Icon>
                  </IconButton>
                </Paper>

                {/* Items List */}
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 3 }}
                >
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDTypography variant="h6" fontWeight="bold">
                      Order Items
                    </MDTypography>
                    <Chip
                      label={`${(sale.items || []).length} items`}
                      size="small"
                      variant="outlined"
                    />
                  </MDBox>

                  <MDBox display="flex" flexDirection="column" gap={1.5}>
                    {(sale.items || []).map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderRadius: 2,
                          bgcolor: "grey.50",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <MDBox flex={1} pr={2}>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                          >
                            {item.product}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            Qty: {item.quantity} ×{" "}
                            {formatCurrency(item.unit_price)}
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="button" fontWeight="bold">
                          {formatCurrency(item.total)}
                        </MDTypography>
                      </Box>
                    ))}
                  </MDBox>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OpsSaleOrderPage;
